import { checkLTLForAll } from '../ltl-shipping-item';
import { calculateShippingRate, ovrCartTotal } from './shipping-tiers';

// The server-rendered grand total already nets every discount (coupons and
// automatic promotions), tax and gift wrap, so building on it avoids re-deriving
// the discount — which would double-count when a coupon and an automatic
// promotion both apply. Cache it on the node the first time we touch it so
// repeated passes never compound: both the drawer HTML and the cart page's
// [data-cart-totals] are replaced wholesale on every refresh, so the attribute
// dies with the node and can never go stale. Attribute writes do not fire the
// .side-cart-details childList observer.
// `cachedServerTotalText` owns the cache; `cachedServerTotal` is a numeric
// convenience over it for the sites that add shipping to it.
function cachedServerTotalText(grandTotalEl) {
    if (!grandTotalEl.dataset.ovrServerTotal) {
        grandTotalEl.dataset.ovrServerTotal = grandTotalEl.textContent;
    }
    return grandTotalEl.dataset.ovrServerTotal;
}

function cachedServerTotal(grandTotalEl) {
    return parseFloat(cachedServerTotalText(grandTotalEl).replace(/[^0-9.-]+/g, '')) || 0;
}

/**
 * The shipping cost already folded into the cart page's server-rendered grand
 * total, read off the estimator row. Zero when no quote is applied yet (the row
 * renders an "Add Info" button instead of a cost, per
 * templates/components/cart/shipping-estimator.html).
 *
 * Scoped to .cart-total--flex-start — the estimator row's own modifier — because
 * the row OVR injects (#cart-ovr-shipping-line) carries the same "Shipping:"
 * label, and reading that one would subtract the flat rate instead of the quote.
 * @returns {number}
 */
function cartEstimatorShipping() {
    const value = document.querySelector('.cart-totals .cart-total--flex-start .shipping-estimate-value');
    return value ? (parseFloat(value.textContent.replace(/[^0-9.-]+/g, '')) || 0) : 0;
}

/**
 * The cart page's grand total with the server's shipping component swapped for
 * the OVR flat rate. That swap is the whole calculation: everything else — tax,
 * coupons, automatic promotions, gift wrap — is already inside the server total,
 * so nothing has to be scraped back out of the totals rows and nothing can be
 * double-counted.
 * @param {Element} grandTotalEl - the .cart-total-value--grand-total span
 * @param {number} ovrShippingRate - flat rate to charge (0 when shipping is free)
 * @returns {number}
 */
export function cartGrandTotal(grandTotalEl, ovrShippingRate) {
    return cachedServerTotal(grandTotalEl) - cartEstimatorShipping() + ovrShippingRate;
}

/**
 * Show or hide ONLY the flat-rate banner, leaving the injected Shipping totals
 * row and the estimator alone. This is not hideOvrElements: an under-threshold
 * loyalty member still owes the flat rate, so their totals row must survive
 * while the banner's copy moves into the loyalty box.
 *
 * The selectors are deliberately byte-identical to updateOvrContent's, because
 * id="show_OVR_Shipping" is emitted by four templates (cart tr, stock.html,
 * compare.html, quick-results.html) — matching a different element than the one
 * that was revealed would leave a stray banner behind.
 * @param {boolean} visible
 * @param {*} slideOutCart - non-null selects the drawer banner
 */
export function setOvrBannerVisible(visible, slideOutCart = null) {
    const banner = document.querySelector(slideOutCart != null
        ? '#show_OVR_Shipping_slide_out_cart'
        : '#show_OVR_Shipping');
    if (banner) {
        banner.style.display = visible ? 'table-row' : 'none';
    }
}

/**
 * Hide OVR shipping elements (used when LTL items are in cart or OVR shipping is not applicable)
 * @param {boolean} slideOutCart - Whether this is for the slide-out cart or regular cart page
 */
export function hideOvrElements(slideOutCart = null){
    //console.log("Hiding OVR Shipping Elements");

    if(slideOutCart != null){
        // Hide slide-out cart OVR elements
        const showShippingEl = document.querySelector("#show_OVR_Shipping_slide_out_cart");
        if(showShippingEl != null){
            showShippingEl.style.display = "none";
        }

        // Undo the drawer totals updateOvrContent wrote: remove the injected
        // shipping line and restore #side-cart-total to the server-rendered value.
        // Only when the line exists — otherwise server totals are untouched.
        const drawerShippingLine = document.querySelector('#side-cart-shipping-line');
        if (drawerShippingLine) {
            drawerShippingLine.remove();
            const subtotalEl = document.querySelector('#side-cart-subtotal');
            const grandTotalEl = document.querySelector('#side-cart-total');
            if (subtotalEl && grandTotalEl) {
                grandTotalEl.textContent = cachedServerTotalText(grandTotalEl);
            }
        }
    }
    else{
        // Hide regular cart page OVR elements
        const showShippingEl = document.querySelector("#show_OVR_Shipping");
        if(showShippingEl != null){
            showShippingEl.style.display = "none";
        }

        // Remove the dynamically created OVR shipping line from cart totals
        const ovrShippingLine = document.querySelector('#cart-ovr-shipping-line');
        if (ovrShippingLine) {
            ovrShippingLine.remove();
            //console.debug('Removed OVR shipping line from cart totals');

            // Undo the grand total updateOvrContent wrote: recompute with
            // shipping = 0, mirroring its own basis. Scoped to line-existed so
            // server-rendered totals stay untouched.
            const grandTotalEl = document.querySelector('.cart-total-value--grand-total span');
            if (grandTotalEl) {
                const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
                grandTotalEl.textContent = formatter.format(cartGrandTotal(grandTotalEl, 0));
            }
        }

        // Show the original shipping estimator line back
        const cartTotalsContainer = document.querySelector('.cart-totals');
        if (cartTotalsContainer) {
            const existingShippingEstimatorLine = cartTotalsContainer.querySelector('.cart-total--flex-start');
            if (existingShippingEstimatorLine) {
                const shippingLabel = existingShippingEstimatorLine.querySelector('.cart-total-label strong');
                // Verify this is the shipping line by checking the label text
                if (shippingLabel && shippingLabel.textContent.toLowerCase().includes('shipping')) {
                    existingShippingEstimatorLine.style.display = '';
                    //console.debug('Restored shipping estimator line');
                }
            }
        }
    }
}
export async function getDetailsOvrPricing(product, url) {
    try {
        const sku = product?.sku?.endsWith("BC") ? product.sku.slice(0, -2) : product.sku;
        const productTotal = product.price?.without_tax?.value || 0;
        const { rate, remaining } = calculateShippingRate(productTotal);
        const isLTL = await checkLTLForAll([sku], url);

        if (!isLTL) {
            await updateOvrContent(rate, remaining);
        }
        else{
            //console.log("Product is LTL, hiding OVR elements: getDetailsOvrPricing");
            hideOvrElements();
        }
    } catch (error) {
        console.error("Error in getDetailsOvrPricing:", error);
    }
}
export async function getCartPageOvrPricing(cartItems, url, slideOutCart = null) {
    console.debug('getCartPageOvrPricing cartItems: ', cartItems);
    try {
        if(cartItems == null){
            console.log("Product is LTL, hiding OVR elements: getCartPageOvrPricing");
            hideOvrElements(slideOutCart);
            return;
        }
        const cartTotal = ovrCartTotal(cartItems);
        const skus = [];

        for (const item of cartItems) {
            if (item?.sku) skus.push(item.sku);
        }

        const { rate, remaining } = calculateShippingRate(cartTotal);
        const isLTL = await checkLTLForAll(skus, url);

        if (!isLTL) {
            await updateOvrContent(rate, remaining,slideOutCart);
            // Loyalty owns the shipping copy for allowed members: hide the banner
            // it just revealed, but keep updateOvrContent's totals row — an
            // under-threshold member still owes the flat rate. Scoped to this
            // entry point on purpose: getDetailsOvrPricing shares the same
            // #show_OVR_Shipping id for the PDP's "Shipping as low as $X".
            // updateOvrContent has no awaits and browsers do not paint between
            // microtasks, so reveal-then-hide cannot flash.
            if (window.loyaltyOwnsShippingCopy === true) {
                setOvrBannerVisible(false, slideOutCart);
            }
        }
        else{
            //console.log("Rate:", rate, " Remaining:", remaining);
            //console.log("Product is LTL, hiding OVR elements: getCartPageOvrPricing !isLTL");
            hideOvrElements(slideOutCart);
        }
    } catch (error) {
        //console.error("Error in getCartPageOvrPricing:", error);
    }
}
export async function updateOvrContent(shippingRate, additionalAvailable, slideOutCart = null) {
    // This function handles only the EARNED case: the whole OVR treatment
    // (banner + injected row + grand total) goes away once the loyalty eligible
    // subtotal reaches the threshold. Guests never set this flag.
    // Flag maintained by custom/loyalty/shipping-calc.js after each calc.
    // NOT the whole story for under-threshold members: since 2026-07-29 a member
    // whose loyalty box renders its own flat-rate sentence also loses the banner
    // — that suppression reads window.loyaltyOwnsShippingCopy in
    // getCartPageOvrPricing above, and deliberately keeps the totals row below.
    if (window.loyaltyFreeShippingQualified === true) {
        hideOvrElements(slideOutCart);
        return;
    }
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    });

    // Determine if the rate is a promotional message (string) or a price (number)
    const isPromotionalMessage = typeof shippingRate === 'string';

    if (slideOutCart != null) {
        // --- Slide-out cart (mini cart) elements ---
        const shippingEl = document.querySelector("#ovr-shipping-total_slide_out_cart");
        const additionalEl = document.querySelector("#ovr-additional-available_slide_out_cart");
        const itemsToAddEl = document.querySelector("#OVRItemsToAddTotal_slide_out_cart");
        const showShippingEl = document.querySelector("#show_OVR_Shipping_slide_out_cart");
        const ovrShippingMessageEl = document.querySelector('#ovr-shipping-message_slide_out_cart');
        const maxShippingCostElement = document.querySelector('#OVRItemsToAddMAXTotal_slide_out_cart');

        if (!showShippingEl || !shippingEl) return;

        // Update the shipping rate display
        if (isPromotionalMessage) {
            // Display promotional message (e.g., "FREE SHIPPING")
            shippingEl.textContent = formatter.format(0.00);
            if (ovrShippingMessageEl) {
                ovrShippingMessageEl.innerHTML = `Your Order Qualifies for <strong id="ovr-shipping-total_slide_out_cart">${shippingRate}</strong>`;
            }
        } else {
            // Display numeric shipping cost
            shippingEl.textContent = formatter.format(shippingRate);
            if (ovrShippingMessageEl) {
                ovrShippingMessageEl.innerHTML = `Your Order Qualifies for <strong id="ovr-shipping-total_slide_out_cart">${formatter.format(shippingRate)}</strong> Flat Rate Shipping`;
            }
        }

        // Update the "additional items" message
        if (typeof additionalAvailable === 'string') {
            // Max tier reached - show the max tier message
            if (itemsToAddEl) {
                itemsToAddEl.style.display = 'none';
            }
            if (maxShippingCostElement != null) {
                maxShippingCostElement.style.display = "table-row";
            }
        } else if (additionalEl) {
            // Still room to add more items at this tier
            additionalEl.textContent = formatter.format(additionalAvailable);
            if (maxShippingCostElement != null) {
                maxShippingCostElement.style.display = "none";
            }
            if (itemsToAddEl) {
                itemsToAddEl.style.display = 'table-row';
            }
        }
        showShippingEl.style.display = "table-row";
        // --- End of original logic ---


        // --- START: Updated logic to add Shipping line and calculate Grand Total ---

        // 1. Select the key elements in the cart summary
        const detailsContainer = document.querySelector('.side-cart-details');
        const subtotalEl = document.querySelector('#side-cart-subtotal');
        const grandTotalEl = document.querySelector('#side-cart-total');

        if (detailsContainer && subtotalEl && grandTotalEl) {
            // 2. Find the shipping line. If it doesn't exist, create and insert it.
            let shippingLine = detailsContainer.querySelector('#side-cart-shipping-line');
            if (!shippingLine) {
                shippingLine = document.createElement('span');
                shippingLine.className = 'side-cart-detail';
                shippingLine.id = 'side-cart-shipping-line';

                shippingLine.innerHTML = `
                    <span>Shipping:</span>
                    <span id="side-cart-shipping-value"></span>
                `;

                // Insert the new line just before the subtotal line
                detailsContainer.insertBefore(shippingLine, subtotalEl.parentElement);
            }

            // 3. Update the shipping cost value
            // Use numeric value for calculations even if promotional message is shown
            const numericShippingRate = isPromotionalMessage ? 0 : shippingRate;
            const shippingValueEl = shippingLine.querySelector('#side-cart-shipping-value');
            if (shippingValueEl) {
                //console.debug('Updating shipping value to:', formatter.format(numericShippingRate));
                shippingValueEl.textContent = formatter.format(numericShippingRate);
            }

            // 4. Calculate the new grand total from the server-rendered value (already
            // net of discounts and tax) plus shipping
            const newGrandTotal = cachedServerTotal(grandTotalEl) + numericShippingRate;
            grandTotalEl.textContent = formatter.format(newGrandTotal);
        }
        // --- END: Updated logic ---
    } else {
        // --- Regular cart page elements ---
        const shippingEl = document.querySelector("#ovr-shipping-total");
        const additionalEl = document.querySelector("#ovr-additional-available");
        const itemsToAddEl = document.querySelector("#OVRItemsToAddTotal");
        const showShippingEl = document.querySelector("#show_OVR_Shipping");
        const ovrShippingMessageEl = document.querySelector('#ovr-shipping-message');

        if (!showShippingEl || !shippingEl) return;

        // Update the shipping rate display
        if (isPromotionalMessage) {
            // Display promotional message (e.g., "FREE SHIPPING")
            shippingEl.textContent = shippingRate;
            if (ovrShippingMessageEl) {
                ovrShippingMessageEl.innerHTML = `Your Order Qualifies for <strong id="ovr-shipping-total">${shippingRate}</strong>`;
            }
        } else {
            // Display numeric shipping cost
            shippingEl.textContent = formatter.format(shippingRate);
            if (ovrShippingMessageEl) {
                ovrShippingMessageEl.innerHTML = `Your Order Qualifies for <strong id="ovr-shipping-total">${formatter.format(shippingRate)}</strong> Flat Rate Shipping`;
            }
        }

        // Update the "additional items" message
        if (typeof additionalAvailable === 'string') {
            if (itemsToAddEl) itemsToAddEl.textContent = additionalAvailable;
        } else if (additionalEl) {
            additionalEl.textContent = formatter.format(additionalAvailable);
        }

        showShippingEl.style.display = "table-row";

        // --- START: Add Shipping line and calculate Grand Total for cart page ---

        // 1. Select the key elements in the cart totals
        const cartTotalsContainer = document.querySelector('.cart-totals');
        const subtotalEl = document.querySelector('.cart-totals .cart-total:nth-child(2) .cart-total-value span');
        const grandTotalEl = document.querySelector('.cart-total-value--grand-total span');

        if (cartTotalsContainer && subtotalEl && grandTotalEl) {
            // Hide the existing shipping estimator line since OVR shipping is active
            const existingShippingEstimatorLine = cartTotalsContainer.querySelector('.cart-total--flex-start');
            if (existingShippingEstimatorLine) {
                const shippingLabel = existingShippingEstimatorLine.querySelector('.cart-total-label strong');
                // Verify this is the shipping line by checking the label text
                if (shippingLabel && shippingLabel.textContent.toLowerCase().includes('shipping')) {
                    existingShippingEstimatorLine.style.display = 'none';
                    //console.debug('Hidden existing shipping estimator line');
                }
            }

            // 2. Find or create the OVR shipping line in cart totals
            let ovrShippingLine = cartTotalsContainer.querySelector('#cart-ovr-shipping-line');

            if (!ovrShippingLine) {
                // Create the shipping line element
                ovrShippingLine = document.createElement('li');
                ovrShippingLine.className = 'cart-total';
                ovrShippingLine.id = 'cart-ovr-shipping-line';

                ovrShippingLine.innerHTML = `
                    <div class="cart-total-label">
                        <strong>Shipping:</strong>
                    </div>
                    <div class="cart-total-value">
                        <span id="cart-ovr-shipping-value"></span>
                    </div>
                `;

                // Insert before the grand total line (last li)
                const grandTotalLine = cartTotalsContainer.querySelector('.cart-total:last-child');
                if (grandTotalLine) {
                    cartTotalsContainer.insertBefore(ovrShippingLine, grandTotalLine);
                }
            }

            // 3. Update the shipping cost value
            // Use numeric value for calculations even if promotional message is shown
            const numericShippingRate = isPromotionalMessage ? 0 : shippingRate;
            const shippingValueEl = ovrShippingLine.querySelector('#cart-ovr-shipping-value');
            if (shippingValueEl) {
                //console.debug('Updating cart page shipping value to:', formatter.format(numericShippingRate));
                shippingValueEl.textContent = formatter.format(numericShippingRate);
            }

            // 4. Calculate the new grand total: the server total with its
            // shipping component swapped for the flat rate. Replaces a
            // re-derivation from the totals rows (subtotal + rate + tax -
            // discount) whose discount term used querySelector on
            // '.cart-total-label strong', which always matched the first row
            // ("Total Items:") — so coupons never reached the total.
            const newGrandTotal = cartGrandTotal(grandTotalEl, numericShippingRate);
            //console.debug('Cart page grand total calculation:', {shipping: numericShippingRate, grandTotal: newGrandTotal});
            grandTotalEl.textContent = formatter.format(newGrandTotal);
        }
        // --- END: Cart page shipping line and grand total logic ---
    }
}
/**
 * Updates the cart summary (Shipping, Grand Total) using the shipping rate
 * from the shipping banner. If the shipping rate is 0, the shipping line
 * is hidden.
 */
export function updateCartTotalsFromShippingRate() {
    // Free ground earned (loyalty): never resurrect the shipping line from the
    // hidden banner's text on drawer refreshes — clean up instead.
    if (window.loyaltyFreeShippingQualified === true) {
        hideOvrElements(true);
        return;
    }

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    });

    // 1. Get the source of the shipping rate from the banner
    const shippingSourceEl = document.querySelector('#ovr-shipping-total_slide_out_cart');
    
    // Select all necessary elements from the cart summary
    const detailsContainer = document.querySelector('.side-cart-details');
    const subtotalEl = document.querySelector('#side-cart-subtotal');
    const grandTotalEl = document.querySelector('#side-cart-total');

    // Exit if the essential elements aren't available
    if (!shippingSourceEl || !detailsContainer || !subtotalEl || !grandTotalEl) {
        console.warn('Cannot update cart totals: one or more required elements are missing.');
        return;
    }

    // 2. Parse the shipping rate from the source element
    const shippingRate = parseFloat(shippingSourceEl.textContent.replace(/[^0-9.-]+/g, "")) || 0;

    // 3. Find the "Shipping" line element
    let shippingLine = detailsContainer.querySelector('#side-cart-shipping-line');

    // --- NEW LOGIC: Show or hide the line based on the rate ---
    if (shippingRate > 0) {
        // If the rate is positive, we need to show the line.
        
        // If the line doesn't exist yet, create it and insert it.
        if (!shippingLine) {
            shippingLine = document.createElement('span');
            shippingLine.className = 'side-cart-detail';
            shippingLine.id = 'side-cart-shipping-line';
            shippingLine.innerHTML = `
                <span>Shipping:</span>
                <span id="side-cart-shipping-value"></span>
            `;
            detailsContainer.insertBefore(shippingLine, subtotalEl.parentElement);
        }

        // Ensure the line is visible and update its value
        shippingLine.style.display = 'inline-table'; // Use 'block' or '' to ensure visibility
        const shippingValueEl = shippingLine.querySelector('#side-cart-shipping-value');
        if (shippingValueEl) {
            shippingValueEl.textContent = formatter.format(shippingRate);
        }

    } else { // shippingRate is 0
        // If the rate is 0 and the line exists, hide it.
        if (shippingLine) {
            shippingLine.style.display = 'none';
        }
        // If the line doesn't exist, we do nothing, which is correct.
    }
    // --- END OF NEW LOGIC ---
    
    // 4. Calculate the new grand total and update the DOM.
    // This calculation works correctly because shippingRate will be 0 when hidden.
    const newGrandTotal = cachedServerTotal(grandTotalEl) + shippingRate;
    grandTotalEl.textContent = formatter.format(newGrandTotal);
}